/**
 * Pipeline health (mig 397/401, pipeline-health M2, operator go 2026-10-05) — INTERNAL.
 *
 * The system measuring its own generation, per UTC day: articles generated,
 * judged by the gate, held at generation, the hold rate (judged), duplicate
 * (filing, product) pairs — 0 expected since backend 0.3.6 — and the
 * generations the one-article guard skipped. A day with a duplicate also opens
 * a `duplicate_generation` attention item on the dashboard.
 *
 * Hold rate (judged) = held ÷ judged, the QA-gate workstream's definition. It is
 * NOT a false-hold rate (that is a share of holds, measured by audit).
 */
import { useMemo } from 'react';
import { keepPreviousData, useQueries } from '@tanstack/react-query';
import { getMetricSeries, type SeriesRow } from '../../lib/api/analytics.js';
import { addDays, toNumber } from '../../lib/analyticsShape.js';
import { VisibilityTag } from './StatTile.js';

export const PIPELINE_METRICS = [
  'pipeline.articles_generated',
  'pipeline.articles_judged',
  'pipeline.articles_held',
  'pipeline.articles_held_unjudged',
  'pipeline.duplicate_pairs',
  'pipeline.guard_skips',
] as const;

export interface PipelineDay {
  day: string;
  generated: number;
  judged: number;
  held: number;
  /** Held with no gate verdict — a gate error; not part of the rate. */
  unjudged: number;
  /** (held − unjudged) ÷ judged = held AND judged ÷ judged; null when nothing was judged. */
  holdRate: number | null;
  duplicates: number;
  skips: number;
}

/** Sum each metric's per-site rows by day (the selected mastheads), newest first. */
export function pipelineDays(byMetric: Record<string, SeriesRow[] | undefined>): PipelineDay[] {
  const sum = (metric: string) => {
    const m = new Map<string, number>();
    for (const r of byMetric[metric] ?? []) {
      m.set(r.metric_date, (m.get(r.metric_date) ?? 0) + (toNumber(r.value) ?? 0));
    }
    return m;
  };
  const gen = sum('pipeline.articles_generated');
  const judged = sum('pipeline.articles_judged');
  const held = sum('pipeline.articles_held');
  const unjudged = sum('pipeline.articles_held_unjudged');
  const dup = sum('pipeline.duplicate_pairs');
  const skips = sum('pipeline.guard_skips');
  const days = new Set<string>([...gen.keys(), ...dup.keys(), ...skips.keys()]);
  return Array.from(days)
    .sort((a, b) => (a < b ? 1 : -1))
    .map((day) => {
      const j = judged.get(day) ?? 0;
      const h = held.get(day) ?? 0;
      const u = unjudged.get(day) ?? 0;
      return {
        day,
        generated: gen.get(day) ?? 0,
        judged: j,
        held: h,
        unjudged: u,
        holdRate: j > 0 ? Math.max(0, h - u) / j : null,
        duplicates: dup.get(day) ?? 0,
        skips: skips.get(day) ?? 0,
      };
    });
}

export function PipelineHealth({ sites, to, stale }: { sites?: string[]; to: string; stale?: boolean }) {
  const from = addDays(to, -13);
  const results = useQueries({
    queries: PIPELINE_METRICS.map((metric) => ({
      queryKey: ['pipeline-health', metric, from, to, sites],
      queryFn: () => getMetricSeries({ metric, sites, dateFrom: from, dateTo: to }),
      placeholderData: keepPreviousData,
      staleTime: 60_000,
    })),
  });
  const loading = results.some((r) => r.isLoading);
  const rows = useMemo(() => {
    const byMetric: Record<string, SeriesRow[] | undefined> = {};
    PIPELINE_METRICS.forEach((m, i) => { byMetric[m] = results[i]?.data?.rows; });
    return pipelineDays(byMetric);
  }, [results]);
  const dupDays = rows.filter((r) => r.duplicates > 0);
  // Gate errors get their own column only when there are any (expected 0).
  const anyUnjudged = rows.some((r) => r.unjudged > 0);

  return (
    <section className={`bg-surface rounded-xl border border-grey-200 p-4 flex flex-col gap-3 ${stale ? 'opacity-60' : ''} transition-opacity`} aria-label="Pipeline health">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-grey-900">Pipeline health</h3>
          <p className="text-xs text-grey-500 mt-0.5">
            The system measuring its own generation, {from} → {to} (UTC days). Hold rate (judged) = articles held
            at generation that the gate judged ÷ articles the gate judged — not a false-hold rate. Duplicate pairs should be 0; any day
            with one is also an attention item on the dashboard.
          </p>
        </div>
        <VisibilityTag visibility="internal" />
      </header>
      {loading ? (
        <p className="text-sm text-grey-400 py-4 text-center">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-grey-400 py-4 text-center">No pipeline facts in this range yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-grey-500">
                <th className="py-1 pr-3 font-medium">Day</th>
                <th className="py-1 pr-3 font-medium text-right">Generated</th>
                <th className="py-1 pr-3 font-medium text-right">Judged</th>
                <th className="py-1 pr-3 font-medium text-right">Held</th>
                {anyUnjudged ? <th className="py-1 pr-3 font-medium text-right" title="held with no gate verdict — a gate error, not in the rate">Unjudged holds</th> : null}
                <th className="py-1 pr-3 font-medium text-right">Hold rate (judged)</th>
                <th className="py-1 pr-3 font-medium text-right">Duplicate pairs</th>
                <th className="py-1 pr-3 font-medium text-right">Guard skips</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.day} className={`border-t border-grey-100 ${r.duplicates > 0 ? 'text-warning-700' : 'text-grey-900'}`}>
                  <td className="py-1 pr-3 font-medium">{r.day}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{r.generated}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{r.judged}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{r.held}</td>
                  {anyUnjudged ? <td className="py-1 pr-3 text-right tabular-nums text-danger-700">{r.unjudged || ''}</td> : null}
                  <td className="py-1 pr-3 text-right tabular-nums">{r.holdRate == null ? '—' : `${(r.holdRate * 100).toFixed(1)}%`}</td>
                  <td className="py-1 pr-3 text-right tabular-nums font-medium">{r.duplicates}{r.duplicates > 0 ? ' ⚠' : ''}</td>
                  <td className="py-1 pr-3 text-right tabular-nums text-grey-600">{r.skips}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <footer className="text-[11px] text-grey-400">
        Pipeline lane of the daily network-metrics sweep · re-reads the last 3 days · operator regenerations are not duplicates
        {dupDays.length > 0 ? ` · duplicate days: ${dupDays.map((d) => d.day).join(', ')}` : ''}
      </footer>
    </section>
  );
}
